import {AxiosInstance} from "axios";
import ReminderNotification from "../model/notification/reminder/ReminderNotification";
import {NotificationBaseApiDto, NotificationStatusApiDto} from "./model/rest";
import LeadStatusNotification from "../model/notification/lead/LeadStatusNotification";
import {NotificationType, NotificationTypeEnum} from "../model/notification/NotificationBase";
import AssigneeChangeNotification from "../model/notification/assignee/AssigneeChangeNotification";
import LeadWatcherChangeNotification from "../model/notification/watcher/LeadWatcherChangeNotification";
import NewThirdPartyLeadNotification from "../model/notification/thirdPartyLead/NewThirdPartyLeadNotification";
import SmsNotification from "../model/notification/sms/SmsNotification";
import QuoteCommentNotification from "../model/notification/quote/QuoteCommentNotification";
import QuoteStatusChangedNotification from "../model/notification/quote/QuoteStatusChangedNotification";
import UserMentionNotification from "../model/notification/mention/UserMentionNotification";
import JobUserMentionNotification from "../model/notification/mention/JobUserMentionNotification";
import JobReminderNotification from "../model/notification/reminder/JobReminderNotification";
import JobStatusNotification from "../model/notification/job/JobStatusNotification";
import LeadBDPChangeNotification from "../model/notification/assignee/LeadBDPChangeNotification";
import LeadAgentChangeNotification from "../model/notification/assignee/LeadAgentChangeNotification";
import JobBDPChangeNotification from "../model/notification/assignee/job/JobBDPChangeNotification";
import JobAgentChangeNotification from "../model/notification/assignee/job/JobAgentChangeNotification";
import JobWatcherChangeNotification from "../model/notification/watcher/JobWatcherChangeNotification";
import JobCreatedFromQuoteNotification from "../model/notification/job/JobCreatedFromQuoteNotification";
import * as moment from 'moment';
import {FilterState} from "../components/layout/header/notification/NotificationDropdown";
import {store} from "../navigation/ReduxProvider";
import {notificationsIsUnreadFetched} from "../store/action/notification/notifications";
import {notifyBugsnag} from "../utils/Utils";
import InvoiceCreatedNotification from "../model/notification/invoice/InvoiceCreatedNotification";
import BillCreatedNotification from "../model/notification/invoice/BillCreatedNotification";
import PaymentArrivedNotification from "../model/notification/payment/PaymentArrivedNotification";
import InvoicePaidNotification from "../model/notification/invoice/InvoicePaidNotification";
import BillPaidNotification from "../model/notification/invoice/BillPaidNotification";

/**
 * Api for note notification.
 */
export default class NotificationApi {
	constructor(public readonly axiosInstance: AxiosInstance) { }

	/**
	 * Fetch notifications.
	 * @returns {Promise<ReminderNotification[]>} Promise containing all notification.
	 */
	fetchNotifications(page: number,
		status: FilterState,
		visibleFrom: moment.Moment): Promise<{ items: Array<NotificationType>, totalItems: number }> {
		const url = `/om/api/web/notifications`;
		const config = {
			params:
				{
					page,
					notificationReadStatus: status.toUpperCase(),
					visibleFrom: visibleFrom.toISOString()
				}
		};
		return this.axiosInstance.get(url, config).then(
			resp => {return {...resp.data, items: mapResponseToNotification(resp.data.items)}})
	}

	fetchNewNotifications(from: moment.Moment, to: moment.Moment) {
		const url = `/om/api/web/notifications/between`;
		return this.axiosInstance.get(url, {params: {fromDate: from.toISOString(), toDate: to.toISOString()}})
			.then(resp => mapResponseToNotification(resp.data))
	}

	/**
	 * Set notifications read or unread
	 * @param {NotificationStatusApiDto[]} data notifications status
	 * @returns {Promise<void>}
	 */
	setStatus(data: NotificationStatusApiDto[]): Promise<any> {
		const url = `/om/api/web/notifications`;
		return this.axiosInstance.put(url, data)
	}

	/**
	 * Mark all notification as read
	 */
	markAllAsRead(visibleFrom: moment.Moment) {
		const url = `/om/api/web/notifications/mark_as_read`;
		return this.axiosInstance.post(url, {dateFrom: visibleFrom.toISOString()});
	}

	checkIfAnyNotificationIsUnread(visibleFrom: moment.Moment): Promise<any> {
		const url = `/om/api/web/notifications/exists_unread`;
		return this.axiosInstance.get(url, {params: {dateTo: visibleFrom.toISOString()}})
			.then(resp => store.dispatch(notificationsIsUnreadFetched(resp.data)))
			.catch(notifyBugsnag)
	}
}

const mapResponseToNotification = (notifications: NotificationBaseApiDto[]): Array<NotificationType> => {
	return notifications.map((notification: NotificationBaseApiDto) => {
		switch (notification.type) {
			case NotificationTypeEnum.Note:
				return ReminderNotification.fromJson(notification);
			case NotificationTypeEnum.LeadStatus:
				return LeadStatusNotification.fromJson(notification);
			case NotificationTypeEnum.LeadAssignees:
				return AssigneeChangeNotification.fromJson(notification);
			case NotificationTypeEnum.LeadBDPAssignee:
				return LeadBDPChangeNotification.fromJson(notification);
			case NotificationTypeEnum.LeadAgentAssignee:
				return LeadAgentChangeNotification.fromJson(notification);
			case NotificationTypeEnum.LeadWatchers:
				return LeadWatcherChangeNotification.fromJson(notification);
			case NotificationTypeEnum.NewThirdPartyLead:
				return NewThirdPartyLeadNotification.fromJson(notification);
			case NotificationTypeEnum.IncomingSmsNotification:
				return SmsNotification.fromJson(notification);
			case NotificationTypeEnum.QuoteCommentCreated:
				return QuoteCommentNotification.fromJson(notification);
			case NotificationTypeEnum.QuoteStatusChanged:
				return QuoteStatusChangedNotification.fromJson(notification);
			case NotificationTypeEnum.UserMentioned:
				return UserMentionNotification.fromJson(notification);
			case NotificationTypeEnum.JobUserMentioned:
				return JobUserMentionNotification.fromJson(notification);
			case NotificationTypeEnum.JobNote:
				return JobReminderNotification.fromJson(notification);
			case NotificationTypeEnum.JobStatus:
				return JobStatusNotification.fromJson(notification);
			case NotificationTypeEnum.JobBDPAssignee:
				return JobBDPChangeNotification.fromJson(notification);
			case NotificationTypeEnum.JobAgentAssignee:
				return JobAgentChangeNotification.fromJson(notification);
			case NotificationTypeEnum.JobWatchers:
				return JobWatcherChangeNotification.fromJson(notification);
			case NotificationTypeEnum.JobCreatedFromQuote:
				return JobCreatedFromQuoteNotification.fromJson(notification);
			case NotificationTypeEnum.InvoiceCreated:
				return InvoiceCreatedNotification.fromJson(notification);
			case NotificationTypeEnum.BillCreated:
				return BillCreatedNotification.fromJson(notification);
			case NotificationTypeEnum.PaymentArrived:
				return PaymentArrivedNotification.fromJson(notification);
			case NotificationTypeEnum.InvoicePaid:
				return InvoicePaidNotification.fromJson(notification);
			case NotificationTypeEnum.BillPaid:
				return BillPaidNotification.fromJson(notification);
			default:
				// Unknown notification fallback.
				return null
		}
	}).filter((el: NotificationType) => el != null)
}